import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import {
  contactoService,
  Contacto,
  TipoDeContacto,
  TIPO_CONTACTO_OPTIONS,
} from '../services/contactoService';
import { useClienteSucursalFilter } from '../hooks/useClienteSucursalFilter';
import Notification from '../components/Notification';

function toLocalInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ContactoFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(id) && location.pathname.includes('/editar');
  const isViewOnly = Boolean(id) && !isEditing;
  const needsLoad = Boolean(id);

  const [loading, setLoading] = useState(needsLoad);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [tipoDeContacto, setTipoDeContacto] = useState<number>(TipoDeContacto.Email);
  const [fecha, setFecha] = useState(toLocalInputValue(new Date()));
  const [clienteId, setClienteId] = useState<number | ''>('');
  const [sucursalId, setSucursalId] = useState<number | ''>('');
  const [detalles, setDetalles] = useState('');
  const [usuarioId, setUsuarioId] = useState(0);
  const [activo, setActivo] = useState(true);
  const [existing, setExisting] = useState<Contacto | null>(null);

  const { clienteOptions, sucursalOptions, loadSucursalesForCliente } = useClienteSucursalFilter();

  const readOnly = isViewOnly;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const c = await contactoService.getById(Number(id));
        if (cancelled) return;
        setExisting(c);
        setTipoDeContacto(c.tipoDeContacto);
        setFecha(toLocalInputValue(new Date(c.fecha)));
        setDetalles(c.detalles || '');
        setUsuarioId(c.usuarioId);
        setActivo(c.activo);
        setClienteId(c.clienteId || '');
        setSucursalId(c.sucursalId || '');
        if (c.clienteId) await loadSucursalesForCliente(c.clienteId);
      } catch (e: any) {
        if (!cancelled) {
          setNotification({
            type: 'error',
            message: e?.message || 'No se pudo cargar el contacto',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadSucursalesForCliente]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (!sucursalId) {
      setNotification({ type: 'error', message: 'Seleccioná una sucursal.' });
      return;
    }
    setSaving(true);
    setNotification(null);
    try {
      if (isEditing && id) {
        await contactoService.update(Number(id), {
          id: Number(id),
          tipoDeContacto,
          fecha: new Date(fecha).toISOString(),
          detalles: detalles || undefined,
          sucursalId: Number(sucursalId),
          usuarioId: usuarioId || existing?.usuarioId || 0,
          activo,
        });
        setNotification({ type: 'success', message: 'Contacto actualizado' });
      } else {
        await contactoService.create({
          tipoDeContacto,
          fecha: new Date(fecha).toISOString(),
          detalles: detalles || undefined,
          sucursalId: Number(sucursalId),
        });
        setNotification({ type: 'success', message: 'Contacto creado' });
      }
      setTimeout(() => navigate('/contactos'), 600);
    } catch (err: any) {
      setNotification({
        type: 'error',
        message: err?.message || 'Error al guardar',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/contactos')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {readOnly ? 'Contacto' : isEditing ? 'Editar contacto' : 'Nuevo contacto'}
          </h1>
          {existing && <p className="text-sm text-gray-500">#{existing.id}</p>}
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Fecha y hora
            </label>
            <input
              type="datetime-local"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              disabled={readOnly}
              required
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Tipo de contacto
            </label>
            <select
              value={tipoDeContacto}
              onChange={(e) => setTipoDeContacto(Number(e.target.value))}
              disabled={readOnly}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm disabled:opacity-60"
            >
              {TIPO_CONTACTO_OPTIONS.filter(
                (t) => t.id !== TipoDeContacto.FinalizacionVisitaTecnica || isEditing,
              ).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </select>
            {tipoDeContacto === TipoDeContacto.VisitaTecnica && !isEditing && (
              <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                Se creará una solicitud pendiente de visita técnica.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Cliente
            </label>
            <select
              value={clienteId}
              onChange={async (e) => {
                const v = e.target.value === '' ? '' : Number(e.target.value);
                setClienteId(v);
                setSucursalId('');
                await loadSucursalesForCliente(v === '' ? undefined : v);
              }}
              disabled={readOnly}
              required
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm disabled:opacity-60"
            >
              <option value="">Seleccionar…</option>
              {clienteOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Sucursal
            </label>
            <select
              value={sucursalId}
              onChange={(e) => setSucursalId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={readOnly || !clienteId}
              required
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm disabled:opacity-60"
            >
              <option value="">Seleccionar…</option>
              {sucursalOptions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Detalles
          </label>
          <textarea
            value={detalles}
            onChange={(e) => setDetalles(e.target.value)}
            disabled={readOnly}
            rows={4}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm disabled:opacity-60"
          />
        </div>

        {!readOnly && (
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate('/contactos')}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        )}

        {readOnly && (
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/contactos/${id}/editar`)}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Editar
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactoFormPage;
